/**
 * «Звонки»: вкладки «Неотвеченные» — что стало с каждым, кто звонил за день
 * (спека `docs/specs/2026-09-24-calls-design.md`; числа — те же, что у
 * `prokat-runner calls-report`), «Кто не взял» — рейтинг сотрудников по накопителю
 * `call_missed` и сами непринятые звонки под ним
 * (`docs/specs/2026-10-08-call-missed-design.md`) и «Звонки» — список `bitrix_call`
 * (`docs/specs/2026-09-26-calls-tabs-design.md`).
 */
import { useQuery } from "@tanstack/react-query";
import { PhoneOutgoing } from "lucide-react";
import { Fragment, type RefObject, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { DateRangePicker } from "@/components/DateRangePicker";
import { bitrixUrl, type CallsDays, type CallsReport, type DaysGroup, type Dial, type DialCount, type DialKind, type Funnel as FunnelData, type Group, loadCall, loadCalls, loadCallsDays, loadMissedRating, type MissedCall, type MissedNext, type MissedRating, NOT_COUNTED, type NoneReason, type NumberRow, type Outcome, type Outgoing, type StaffAt, type StaffState, type Step } from "@/lib/calls";
import { shortTime, workTime } from "@/lib/leads";
import { Card } from "@/pages/CallCardPage";
import { CallsListTab } from "@/pages/CallsList";

const GROUP: Record<Group, string> = { answered: "отвечен", short: "пропущен ≤ 20 с", long: "пропущен > 20 с" };
const OUTCOME: Record<Outcome, string> = { in_time: "за 20 мин", late: "позже", none: "не перезвонили" };
const TYPE: Record<number, string> = { 1: "исходящий", 2: "входящий", 3: "входящий (переадр.)", 4: "callback", 5: "авто" };

/** Фильтр таблицы номеров: кнопка над таблицей или клик по ячейке воронки. */
type Filter = { key: string; label: string; keep: (n: NumberRow) => boolean };
const FILTERS: Filter[] = [
  { key: "missed_long", label: "пропущены > 20 с", keep: (n) => n.group === "long" },
  { key: "missed", label: "все пропущенные", keep: (n) => n.group !== "answered" },
  { key: "none", label: "не перезвонили", keep: (n) => notCalledBack(n) },
  { key: "all", label: "все номера", keep: () => true },
];
/** «Не перезвонили» — без невалидных номеров и «пытались» (владелец 26.09.26). */
function notCalledBack(n: NumberRow): boolean {
  return n.outcome === "none" && !NOT_COUNTED.includes(n.none_reason!);
}

/** По умолчанию — «не перезвонили»: главный вопрос экрана (решение владельца 24.09.26). */
const DEFAULT_FILTER = FILTERS.find((f) => f.key === "none")!;

/** Разбивка «не перезвонили» — плитки над воронкой, фильтр `none:<причина>`. */
const NONE_REASONS: { key: NoneReason; label: string; hint: string }[] = [
  { key: "never", label: "Ни разу не звонили", hint: "наших исходящих нет, клиент больше не звонил" },
  { key: "recalled", label: "Клиент звонил снова", hint: "опять пропуск, а мы так и не позвонили" },
  { key: "tried", label: "Звонили, не дозвонились", hint: "1–2 попытки; время последней — в таблице" },
];
/** Без разговора, но не «не перезвонили» — серые плитки справа. */
const OTHER_REASONS: { key: NoneReason; label: string; hint: string }[] = [
  { key: "attempted", label: "Пытались", hint: "3 и больше наших попыток, не дозвонились" },
  { key: "invalid", label: "Невалидные", hint: "заграничный, казахстанский или неполный номер" },
];

/** Сортировка таблицы номеров кликом по заголовку; `null` — порядок первого звонка. */
type SortKey = "calls" | "wait" | "episode" | "attempt";
const SORT_VALUE: Record<SortKey, (n: NumberRow) => string | number | null> = {
  calls: (n) => n.calls,
  wait: (n) => (n.missed ? n.max_wait : null),
  episode: (n) => n.episode_from,
  // «Кому давно не звонили»: без попыток — раньше всех, номера без пропуска — в конце.
  attempt: (n) => (n.episode_from === null ? null : (n.last_attempt_at ?? "")),
};

function sortRows(rows: NumberRow[], sort: string | null): NumberRow[] {
  const desc = sort?.startsWith("-") ?? false;
  const value = SORT_VALUE[(sort?.replace(/^-/, "") ?? "") as SortKey];
  if (!value) return rows;
  return [...rows].sort((a, b) => {
    const x = value(a);
    const y = value(b);
    if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1; // пустые — всегда в конце
    return (x < y ? -1 : x > y ? 1 : 0) * (desc ? -1 : 1);
  });
}

/** Сегодня по локальному календарю, ГГГГ-ММ-ДД. */
const today = () => new Date().toLocaleDateString("sv-SE");

/** День, фильтр и сортировка — в адресе: ссылку можно переслать сотруднику. */
function useCallsParams() {
  const [params, setParams] = useSearchParams();
  const p = { date: params.get("date") || today(), filter: params.get("filter") || DEFAULT_FILTER.key, sort: params.get("sort") };
  const set = (next: Partial<typeof p>) => {
    const v = { ...p, ...next };
    const q: Record<string, string> = { date: v.date, filter: v.filter };
    if (v.sort) q.sort = v.sort;
    setParams(q, { replace: true });
  };
  return [p, set] as const;
}

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : "—");

/** Порог доли ответа (решение владельца 24.09.26): > 85% — хорошо, > 70% — терпимо, иначе плохо. */
function answerTone(n: number, of: number): string {
  if (!of) return "";
  const p = (n / of) * 100;
  return p > 85 ? "text-ok" : p > 70 ? "text-warn" : "text-err";
}

type FunnelStep = {
  key: string;
  label: string;
  hint: string;
  value: (f: FunnelData) => number;
  of: (f: FunnelData) => number;
  step: (s: Step) => boolean;
  tone?: (f: FunnelData) => string;
};

/**
 * Воронка первой строкой: по звонкам и по уникальным номерам (номер — по первому
 * звонку за день). Клик по ячейке оставляет в таблице номера этого шага: в
 * строке «Звонки» — номера, у которых такой звонок был, в строке «Номера» — по
 * первому звонку.
 */
function funnelSteps(r: Pick<CallsReport, "long_wait_sec">): FunnelStep[] {
  const missedOf = (f: FunnelData) => f.reachable - f.answered;
  return [
    { key: "incoming", label: "Входящих", hint: "100%", value: (f) => f.incoming, of: (f) => f.incoming, step: () => true },
    {
      key: "reachable",
      label: `Ждали > ${r.long_wait_sec} с`,
      hint: "от входящих",
      value: (f) => f.reachable,
      of: (f) => f.incoming,
      step: (s) => s !== "short",
    },
    {
      key: "answered",
      label: "Ответили",
      hint: "от ждавших · норма > 85%",
      value: (f) => f.answered,
      of: (f) => f.reachable,
      step: (s) => s === "answered",
      tone: (f) => answerTone(f.answered, f.reachable),
    },
    {
      key: "called_back",
      label: "Перезвонили",
      hint: "от пропущенных > 20 с",
      value: (f) => f.called_back,
      of: missedOf,
      step: (s) => s === "in_time" || s === "late",
    },
    {
      key: "none",
      label: "Не перезвонили",
      hint: "от пропущенных > 20 с",
      value: (f) => f.not_called_back,
      of: missedOf,
      step: (s) => s === "none",
      tone: (f) => (f.not_called_back ? "text-err" : ""),
    },
  ];
}

const FUNNEL_ROWS = { calls: "Звонки", numbers: "Номера" } as const;

/** Фильтр по ключу из адреса: кнопка над таблицей или ячейка воронки `строка:шаг`. */
function filterOf(key: string, r: CallsReport): Filter {
  const own = FILTERS.find((f) => f.key === key);
  if (own) return own;
  const reason = [...NONE_REASONS, ...OTHER_REASONS].find((x) => key === `none:${x.key}`);
  if (reason) {
    const prefix = NOT_COUNTED.includes(reason.key) ? "" : "не перезвонили: ";
    return { key, label: `${prefix}${reason.label.toLowerCase()}`, keep: (n) => n.none_reason === reason.key };
  }
  const [row, step] = key.split(":") as [keyof typeof FUNNEL_ROWS, string];
  const s = funnelSteps(r).find((x) => x.key === step);
  if (!(row in FUNNEL_ROWS) || !s) return DEFAULT_FILTER;
  return {
    key,
    label: `${FUNNEL_ROWS[row].toLowerCase()}: ${s.label.toLowerCase()}`,
    keep: row === "calls" ? (n) => n.steps.some(s.step) : (n) => s.step(n.steps[0]!),
  };
}

/** Главная цифра экрана: кому не перезвонили и почему. Клик — эти номера в таблице. */
function NotCalledBack({ r, active, onPick }: { r: CallsReport; active: string; onPick: (key: string) => void }) {
  const none = r.numbers.filter(notCalledBack);
  const Cell = ({ k, label, value, hint, muted }: { k: string; label: string; value: number; hint: string; muted?: boolean }) => (
    <button
      type="button"
      onClick={() => onPick(k)}
      title="Показать эти номера в таблице ниже"
      className={`flex min-w-0 flex-col gap-1 rounded-[var(--radius-ui)] border p-3 text-left hover:bg-bg-hover ${
        active === k ? "border-fg" : "border-transparent"
      }`}
    >
      <span className="text-[12px] text-fg-muted">{label}</span>
      <span
        className={`text-[26px] font-semibold leading-none tracking-[-0.02em] tabular-nums ${
          muted ? "text-fg-muted" : value ? "text-err" : ""
        }`}
      >
        {value}
      </span>
      <span className="text-[11.5px] text-fg-faint">{hint}</span>
    </button>
  );
  return (
    <section className="flex flex-wrap gap-2" data-annot="не перезвонили">
      <div className="grid flex-[4_1_36rem] grid-cols-1 gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2 sm:grid-cols-2 lg:grid-cols-4">
        <Cell k="none" label="Не перезвонили" value={none.length} hint="был пропуск, разговора с номером нет до сих пор" />
        {NONE_REASONS.map((x) => (
          <Cell key={x.key} k={`none:${x.key}`} label={x.label} value={none.filter((n) => n.none_reason === x.key).length} hint={x.hint} />
        ))}
      </div>
      <div className="grid flex-[2_1_18rem] grid-cols-2 gap-1 rounded-[var(--radius-card)] border border-border bg-bg-subtle p-2" data-annot="не считаем">
        {OTHER_REASONS.map((x) => (
          <Cell key={x.key} k={`none:${x.key}`} label={x.label} value={r.numbers.filter((n) => n.none_reason === x.key).length} hint={x.hint} muted />
        ))}
      </div>
    </section>
  );
}

function Funnel({ r, onPick }: { r: CallsReport; onPick: (key: string) => void }) {
  const steps = funnelSteps(r);
  const rows: { key: "calls" | "numbers"; label: string; f: FunnelData }[] = [
    { key: "calls", label: FUNNEL_ROWS.calls, f: r.funnel.calls },
    { key: "numbers", label: FUNNEL_ROWS.numbers, f: r.funnel.numbers },
  ];
  const pick = (row: "calls" | "numbers", s: FunnelStep) => onPick(`${row}:${s.key}`);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-4" data-annot="воронка входящих">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-3 py-1.5 font-medium" />
              {steps.map((s) => (
                <th key={s.key} className="whitespace-nowrap px-3 py-1.5 font-medium">
                  <div className="text-[12.5px] text-fg-muted">{s.label}</div>
                  <div className="font-normal">{s.hint}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(({ key, label, f }) => (
              <tr key={key} className="border-t border-border" data-annot={`воронка: ${label}`}>
                <td className="whitespace-nowrap px-3 py-2 text-fg-muted">{label}</td>
                {steps.map((s) => {
                  const tone = s.tone?.(f) ?? "";
                  return (
                    <td key={s.key} className="whitespace-nowrap px-1 py-1">
                      <button
                        type="button"
                        onClick={() => pick(key, s)}
                        title="Показать эти номера в таблице ниже"
                        className="w-full rounded-[var(--radius-ui)] px-2 py-1 text-left hover:bg-bg-hover"
                      >
                        <span className={`text-[22px] font-semibold tabular-nums tracking-[-0.02em] ${tone}`}>
                          {s.value(f)}
                        </span>
                        <span className={`ml-2 text-[13px] tabular-nums ${tone || "text-fg-muted"}`}>
                          {pct(s.value(f), s.of(f))}
                        </span>
                        {s.key === "called_back" && (
                          <div className="text-[11.5px] text-fg-faint">
                            за {r.callback_min_norm} мин: {f.called_back_in_time}
                          </div>
                        )}
                        {s.key === "none" && (f.attempted > 0 || f.invalid > 0) && (
                          <div className="text-[11.5px] text-fg-faint">
                            не считаем: пытались {f.attempted}, невалидные {f.invalid}
                          </div>
                        )}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[11.5px] text-fg-faint">
        Номер считается по первому звонку за день. Перезвонили — после пропуска с номером состоялся разговор (наш
        дозвон или клиент сам дозвонился). Клик по числу — эти номера в таблице ниже.
      </div>
    </section>
  );
}

/** Ссылки на лид и сделку в Битриксе; клик не раскрывает строку номера. */
function CrmLinks({ lead, deal }: { lead: number | null; deal: number | null }) {
  if (!lead && !deal) return <span className="text-fg-faint">—</span>;
  const link = (kind: "lead" | "deal", id: number, label: string) => (
    <a
      href={bitrixUrl(kind, id)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="text-fg underline decoration-border underline-offset-2 hover:decoration-fg"
    >
      {label} {id}
    </a>
  );
  return (
    <span className="inline-flex gap-3 whitespace-nowrap">
      {lead ? link("lead", lead, "лид") : null}
      {deal ? link("deal", deal, "сделка") : null}
    </span>
  );
}

/** Одна ссылка в Битрикс по номеру лида или сделки; клик не раскрывает строку номера. */
function CrmLink({ kind, id }: { kind: "lead" | "deal"; id: number | null }) {
  if (!id) return <span className="text-fg-faint">—</span>;
  return (
    <a
      href={bitrixUrl(kind, id)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      className="tabular-nums text-fg underline decoration-border underline-offset-2 hover:decoration-fg"
    >
      {id}
    </a>
  );
}

/** Пометка линии без лидов: на курьерской линии лид в Bitrix отключён намеренно. */
function LineNote({ note }: { note: string | null }) {
  if (!note) return null;
  return (
    <span
      title="На этой линии лиды в Bitrix не создаются намеренно — звонят курьеры"
      className="ml-2 rounded-[var(--radius-ui)] border border-border px-1.5 py-px font-sans text-[11px] text-fg-muted"
    >
      {note}
    </span>
  );
}

/** Номер — ссылка в Битрикс: на сделку, если есть, иначе на лид; без привязок — просто текст. */
function PhoneLink({ n }: { n: NumberRow }) {
  const [kind, id] = n.deal_id ? (["deal", n.deal_id] as const) : (["lead", n.lead_id] as const);
  if (!id) return <>{n.phone}</>;
  return (
    <a
      href={bitrixUrl(kind, id)}
      target="_blank"
      rel="noreferrer"
      onClick={(e) => e.stopPropagation()}
      title={kind === "deal" ? `Сделка ${id} в Битриксе` : `Лид ${id} в Битриксе`}
      className="text-fg underline decoration-border underline-offset-2 hover:decoration-fg"
    >
      {n.phone}
    </a>
  );
}

const BUSY_KIND: Record<string, string> = { in: "вх.", out: "исх.", other: "звонок" };

/** Ответ телефона из лога Voximplant — словами для экрана. */
const SIP: Record<string, string> = { offline: "телефон не в сети", limit: "отбит лимитом вызовов", busy: "телефон занят", decline: "отклонён" };

/** Кто из КЦ не взял, до кого звонок не дошёл, кому звонили мало, кто разговаривал и кто не на линии. */
function StaffAtCall({ staff, at }: { staff: StaffAt[]; at: string }) {
  const day = at.slice(0, 10);
  const person = (s: StaffAt) => (
    <Link key={s.user_id} to={`/activity/${s.user_id}?date=${day}`} className="hover:underline" title="Смена сотрудника">
      {s.name}
    </Link>
  );
  const part = (state: StaffState, label: string, cls: string, extra: (s: StaffAt) => string) => {
    const who = staff.filter((s) => s.state === state);
    if (!who.length) return null;
    return (
      <span className="mr-4" data-annot={`${label} ${at}`}>
        <span className={cls}>{label}: </span>
        {who.map((s, i) => (
          <Fragment key={s.user_id}>
            {i > 0 && ", "}
            {person(s)}
            <span className="text-fg-faint">{extra(s)}</span>
          </Fragment>
        ))}
      </span>
    );
  };
  if (!staff.length) return <span className="text-fg-faint">никого из КЦ на смене</span>;
  return (
    <>
      {part("free", "Не взяли", "font-medium text-warn", (s) => (s.rang_sec ? ` (звонил ${s.rang_sec} с)` : s.free_sec ? ` (в Bitrix ${s.free_sec} с)` : ""))}
      {part("unreached", "Не дошёл", "font-medium text-warn", (s) => ` (шёл ${s.free_sec} с${s.sip && SIP[s.sip] ? `, ${SIP[s.sip]}` : ""}, Bitrix молчал)`)}
      {part("short", "Звонили меньше 10 с", "text-fg", (s) => ` (${s.free_sec} с)`)}
      {part("busy", "Разговаривали", "text-fg", (s) =>
        s.busy_call ? ` (${BUSY_KIND[s.busy_call.kind] ?? s.busy_call.kind} с ${shortTime(s.busy_call.at)})` : "",
      )}
      {part("off", "Не на линии", "text-fg", (s) => ` (${s.status})`)}
    </>
  );
}

/** Карточка звонка прямо под его строкой: круги очереди, запись, расшифровка — та же, что на `/calls/<id>`. */
function CallInline({ id }: { id: number }) {
  const data = useQuery({ queryKey: ["call", id], queryFn: () => loadCall(id) });
  return (
    <div className="sticky left-0 flex flex-col gap-4 rounded-[var(--radius-card)] border border-border bg-bg p-3 md:max-w-[calc(100vw-20rem)]" data-annot={`карточка звонка ${id}`}>
      <Link to={`/calls/${id}`} className="self-start text-[12px] text-accent hover:underline">
        Звонок {id}: открыть отдельной страницей
      </Link>
      {data.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Загружаю карточку…</div>
      ) : data.isError ? (
        <div className="text-[12.5px] text-err">Не удалось загрузить карточку: {String(data.error.message)}</div>
      ) : (
        <Card c={data.data} />
      )}
    </div>
  );
}

function History({ n }: { n: NumberRow }) {
  const [card, setCard] = useState<number | null>(null);
  return (
    <table className="w-full text-[12px]">
      <tbody>
        {n.history.map((c) => (
          <Fragment key={c.id}>
          <tr className="text-fg-muted">
            <td className="whitespace-nowrap py-0.5 pr-4 tabular-nums">
              {c.card_id ? (
                <button
                  type="button"
                  onClick={() => setCard(card === c.card_id ? null : c.card_id)}
                  aria-expanded={card === c.card_id}
                  title="Карточка звонка: кому дозванивались, запись и расшифровка"
                  className="text-accent underline decoration-border underline-offset-2 hover:decoration-fg"
                >
                  {shortTime(c.at)}
                </button>
              ) : (
                shortTime(c.at)
              )}
            </td>
            <td className="whitespace-nowrap py-0.5 pr-4">{TYPE[c.type_id] ?? `тип ${c.type_id}`}</td>
            <td className={`whitespace-nowrap py-0.5 pr-4 ${c.answered ? "text-ok" : "text-warn"}`}>
              {c.answered ? `разговор ${c.talk} с` : "без ответа"}
            </td>
            <td className="whitespace-nowrap py-0.5 pr-4 tabular-nums">ожидание {c.wait} с</td>
            <td className="whitespace-nowrap py-0.5 pr-4">{c.user ?? "—"}</td>
            <td className="py-0.5">
              <CrmLinks lead={c.lead_id} deal={c.deal_id} />
              <LineNote note={c.line_note} />
            </td>
          </tr>
          {c.staff && (
            <tr>
              <td />
              <td colSpan={5} className="pb-1.5 pr-4 text-[11.5px] text-fg-muted">
                <StaffAtCall staff={c.staff} at={c.at} />
              </td>
            </tr>
          )}
          {c.card_id && card === c.card_id && (
            <tr>
              <td colSpan={6} className="pb-2 pt-1">
                <CallInline id={c.card_id} />
              </td>
            </tr>
          )}
          </Fragment>
        ))}
      </tbody>
    </table>
  );
}

const COLUMNS: { label: string; sort?: SortKey; title?: string }[] = [
  { label: "Номер" },
  { label: "Лид", title: "Последний лид в Битриксе по звонкам номера" },
  { label: "Сделка", title: "Последняя сделка в Битриксе по звонкам номера" },
  { label: "Итог дня" },
  { label: "Звонков", sort: "calls" },
  { label: "Макс. ожидание", sort: "wait" },
  { label: "Пропуск с", sort: "episode" },
  { label: "КЦ на линии" },
  { label: "Попытки перезвона", sort: "attempt", title: "Наши исходящие после пропуска до разговора. Сортировка — кому дольше всех не звонили" },
  { label: "Перезвон" },
  { label: "Кто" },
];

/** Клик по заголовку: по возрастанию → по убыванию → без сортировки. */
const nextSort = (cur: string | null, key: SortKey) => (cur === key ? `-${key}` : cur === `-${key}` ? null : key);

/**
 * «КЦ на линии»: кто мог взять во время пропусков. Статус «Работаю» не значит, что звонок дошёл:
 * звонки, разобранные по логу, делятся на «не взяли», «не дошло» и «звонили мало» (владелец 03.11.26).
 */
function OnShift({ n }: { n: NumberRow }) {
  if (n.on_shift === null) return <>—</>;
  if (!n.on_shift) return <span title="Когда начался пропуск, никто из КЦ не стоял в статусе «Работаю» или «Задача (В очереди)»">нет</span>;
  if (n.free == null) return <>да</>;
  const free = n.free.length > 0;
  const unreached = (n.unreached?.length ?? 0) > 0;
  if (!free && !unreached) {
    if (n.short?.length) return <span title={`Свободны, но звонили меньше 10 с: ${n.short.join(", ")}`}>звонили &lt; 10 с</span>;
    return <span title="Все на линии разговаривали">все заняты</span>;
  }
  return (
    <>
      {free && (
        <span className="font-medium text-warn" title={`Свободны, звонок дошёл и не взяли: ${n.free.join(", ")}`}>
          не взяли: {n.free.length}
        </span>
      )}
      {free && unreached && " · "}
      {unreached && (
        <span className="text-warn" title={`Статус «на линии», но телефон не в сети и Bitrix не отозвался: ${n.unreached!.join(", ")}`}>
          не дошло: {n.unreached!.length}
        </span>
      )}
    </>
  );
}

function Attempts({ n }: { n: NumberRow }) {
  if (n.episode_from === null) return <span className="text-fg-faint">—</span>;
  if (!n.attempts)
    return <span className={n.outcome === "none" ? "font-medium text-warn" : "text-fg-faint"}>не звонили</span>;
  return (
    <span className="tabular-nums">
      {n.attempts}
      <span className="text-fg-muted"> · посл. {shortTime(n.last_attempt_at)}</span>
    </span>
  );
}

function Numbers({
  numbers,
  filter,
  sort,
  set,
  anchor,
}: {
  numbers: NumberRow[];
  filter: Filter;
  sort: string | null;
  set: (next: { filter?: string; sort?: string | null }) => void;
  anchor: React.RefObject<HTMLElement | null>;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const rows = sortRows(numbers.filter(filter.keep), sort);
  const fromFunnel = !FILTERS.some((f) => f.key === filter.key);
  return (
    <section ref={anchor} className="scroll-mt-4 rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="детализация по номерам">
      <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-3">
        <h2 className="mr-2 text-[13px] font-medium">Номера: {rows.length}</h2>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => set({ filter: f.key })}
            className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${
              filter.key === f.key ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"
            }`}
          >
            {f.label}
          </button>
        ))}
        {fromFunnel && (
          <span className="inline-flex h-7 items-center rounded-[var(--radius-ui)] border border-fg bg-bg px-2.5 text-[12px] text-fg">
            {filter.label}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="p-4 text-[12.5px] text-fg-muted">Нет номеров.</div>
      ) : (
        <div className="overflow-x-auto">
          {/* Ширина — по содержимому: на широком экране столбцы не разъезжаются (владелец 26.09.26). */}
          <table className="w-auto text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                {COLUMNS.map((c) => (
                  <th key={c.label} className="whitespace-nowrap px-2.5 py-2 font-medium first:pl-4">
                    {c.sort ? (
                      <button
                        type="button"
                        onClick={() => set({ sort: nextSort(sort, c.sort!) })}
                        title={c.title ?? "Сортировать"}
                        className={`hover:text-fg ${sort?.replace(/^-/, "") === c.sort ? "text-fg" : ""}`}
                      >
                        {c.label}
                        {sort === c.sort ? " ↑" : sort === `-${c.sort}` ? " ↓" : ""}
                      </button>
                    ) : (
                      <span title={c.title}>{c.label}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((n) => (
                <Fragment key={n.phone}>
                  <tr
                    className="cursor-pointer border-t border-border hover:bg-bg-hover"
                    onClick={() => setOpen(open === n.phone ? null : n.phone)}
                    data-annot={`номер ${n.phone}`}
                  >
                    <td className="whitespace-nowrap py-1.5 pl-4 pr-2.5 font-mono tabular-nums">
                      <PhoneLink n={n} />
                      <LineNote note={n.line_note} />
                    </td>
                    <td className="px-2.5 py-1.5" data-annot={`лид ${n.phone}`}>
                      <CrmLink kind="lead" id={n.lead_id} />
                    </td>
                    <td className="px-2.5 py-1.5" data-annot={`сделка ${n.phone}`}>
                      <CrmLink kind="deal" id={n.deal_id} />
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-1.5">{GROUP[n.group]}</td>
                    <td className="px-2.5 py-1.5 tabular-nums">
                      {n.calls}
                      {n.missed ? <span className="text-fg-faint"> (пропущ. {n.missed})</span> : null}
                    </td>
                    <td className="px-2.5 py-1.5 tabular-nums">{n.missed ? `${n.max_wait} с` : "—"}</td>
                    <td className="whitespace-nowrap px-2.5 py-1.5 tabular-nums">{shortTime(n.episode_from)}</td>
                    <td className="whitespace-nowrap px-2.5 py-1.5">
                      <OnShift n={n} />
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-1.5">
                      <Attempts n={n} />
                    </td>
                    <td className="whitespace-nowrap px-2.5 py-1.5">
                      {n.outcome === null ? (
                        <span className="text-fg-faint">—</span>
                      ) : n.none_reason === "invalid" ? (
                        <span className="text-fg-muted">невалидный номер</span>
                      ) : n.none_reason === "attempted" ? (
                        <span className="text-fg-muted">пытались</span>
                      ) : n.outcome === "none" ? (
                        <span className="font-medium text-err">ждёт перезвона</span>
                      ) : (
                        <span className={n.outcome === "late" ? "text-fg-muted" : "text-ok"}>
                          {OUTCOME[n.outcome]}: {workTime(n.callback_min)}
                        </span>
                      )}
                    </td>
                    <td className="px-2.5 py-1.5 text-fg-muted">
                      {n.callback_at ? (n.callback_by ?? "сам дозвонился") : "—"}
                    </td>
                  </tr>
                  {open === n.phone && (
                    <tr className="bg-bg">
                      <td colSpan={COLUMNS.length} className="px-4 py-2">
                        <History n={n} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const ddmm = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;

/** Звонок, который сотрудник не взял: время — ссылка на карточку звонка. */
/** Номер в человеческом виде: 79161341539 → +7 916 134-15-39. */
const phoneText = (p: string) =>
  p.length === 11 ? `+${p[0]} ${p.slice(1, 4)} ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9)}` : p;

/** Чем кончился пропущенный: перезвонили мы, клиент позвонил снова или никто. `at` — время
 *  самого пропущенного: окно сутки, поэтому перезвон следующего утра показываем с датой. */
function MissedNextMark({ n, at }: { n: MissedNext | null; at: string }) {
  if (!n)
    return (
      <span className="text-err" title="за сутки после пропущенного по этому номеру никто не звонил">
        не перезвонили
      </span>
    );
  const кто = n.kind === "out" ? "перезвонили" : "клиент позвонил снова";
  const tone = n.answered ? "text-ok" : "text-warn";
  const другой_день = n.at.slice(0, 10) !== at.slice(0, 10);
  const когда = `${другой_день ? `${dm(n.at)} ` : ""}${n.at.slice(11, 16)}`;
  return (
    <span className={tone} title={`${кто} ${когда}${n.user ? ` · ${n.user}` : ""}${n.answered ? ` · ${n.duration} с` : " · без ответа"}`}>
      {n.kind === "out" ? "↩" : "↪"} {когда}
      {!n.answered && " без ответа"}
    </span>
  );
}

/**
 * Непринятый звонок строкой детализации: время — карточка звонка с записью и расшифровкой, номер —
 * поиск по нему (сделка, лид, чаты), «позвонить» открывает софтфон, рядом — чем звонок кончился.
 * Просьба руководителя КЦ 10.10.26: «кликнуть, увидеть номер и перезвонить» и «что происходит дальше».
 */
function MissedRow({ c, name }: { c: MissedCall; name: string }) {
  const where = c.kind === "unreached" ? (SIP[c.sip] ?? "не дошёл") : c.where === "phone" ? "на телефон" : "в Bitrix";
  return (
    <tr className="border-t border-border hover:bg-bg-hover" data-annot={`непринятый звонок ${c.id}`}>
      <td className="whitespace-nowrap px-4 py-1.5 tabular-nums">
        <Link to={`/calls/${c.id}`} className="hover:underline" title="Карточка звонка: запись и расшифровка">
          {dm(c.at)} {c.at.slice(11, 16)}
        </Link>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5">{name}</td>
      <td className="whitespace-nowrap px-3 py-1.5">
        {c.phone ? (
          <span className="inline-flex items-baseline gap-1.5">
            <Link to={`/search?q=${c.phone}`} className="font-mono text-[12px] hover:underline" title="Найти клиента: сделка, лид, чаты">
              {phoneText(c.phone)}
            </Link>
            <a href={`sip:+${c.phone}`} className="text-fg-muted hover:text-fg" title="Позвонить (откроет софтфон)">
              <PhoneOutgoing size={12} className="inline" />
            </a>
          </span>
        ) : (
          <span className="text-fg-faint">—</span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 tabular-nums text-fg-muted" title={c.kind === "unreached" ? `очередь отдавала звонок ${c.free} с, но он не дошёл: ${where}, Bitrix в браузере не отозвался` : `до сотрудника дозванивались ${c.free} с (${where})`}>
        {c.free} с <span className="text-fg-faint">{where}</span>
      </td>
      <td className="whitespace-nowrap px-3 py-1.5 text-fg-muted">{c.line || <span className="text-fg-faint">—</span>}</td>
      <td className="whitespace-nowrap px-4 py-1.5">
        <MissedNextMark n={c.next} at={c.at} />
      </td>
    </tr>
  );
}

/** День минус N дней, ГГГГ-ММ-ДД. */
const shiftDay = (day: string, n: number) =>
  new Date(new Date(`${day}T12:00:00`).getTime() - n * 86_400_000).toLocaleDateString("sv-SE");

/** Что показывает детализация: сотрудник (null — все), период его цифры и вид: не взял или не дошло. */
type Sel = { user: number | null; period: "day" | "week" | "month"; kind: MissedCall["kind"] };

/** Цифра таблицы — кнопка: отбирает детализацию по сотруднику и периоду и прокручивает к ней. */
function Count({ n, pick, sel, on, tone, big }: { n: number; pick: Sel; sel: Sel; on: (p: Sel) => void; tone?: string; big?: boolean }) {
  const active = sel.user === pick.user && sel.period === pick.period && sel.kind === pick.kind;
  if (!n) return <span className="text-fg-faint">—</span>;
  return (
    <button
      type="button"
      onClick={() => on(pick)}
      aria-pressed={active}
      title="Показать эти звонки в детализации ниже"
      className={`rounded-[var(--radius-ui)] px-1 tabular-nums hover:bg-bg-hover hover:underline ${big ? "text-[16px] font-semibold" : ""} ${
        active ? "bg-bg-hover underline" : ""
      } ${tone ?? ""}`}
    >
      {n}
    </button>
  );
}

const PERIODS = ["day", "week", "month"] as const;

/** Четыре ячейки периода: принял, не взял и не дошло (кнопки к детализации), доля не взятых. */
function TookMissed({ took, missed, unreached, pick, sel, on }: { took: number; missed: number; unreached: number; pick: Omit<Sel, "kind">; sel: Sel; on: (p: Sel) => void }) {
  const all = took + missed;
  const day = pick.period === "day";
  return (
    <>
      <td className={`border-l border-border px-3 py-2 text-right tabular-nums ${took ? "text-fg-muted" : "text-fg-faint"}`}>{took || "—"}</td>
      <td className="px-3 py-2 text-right">
        <Count n={missed} pick={{ ...pick, kind: "ask" }} sel={sel} on={on} {...(day && { tone: "text-err", big: true })} />
      </td>
      <td className="px-3 py-2 text-right">
        <Count n={unreached} pick={{ ...pick, kind: "unreached" }} sel={sel} on={on} {...(day && { tone: "text-warn" })} />
      </td>
      <td className={`px-3 py-2 text-right tabular-nums text-[12px] ${missed ? "text-fg-muted" : "text-fg-faint"}`}>
        {all && missed ? `${Math.round((missed / all) * 100)} %` : "—"}
      </td>
    </>
  );
}

/**
 * Кто не взял трубку за выбранный день и за 7 / 30 дней по этот день. Считает фон `call_missed` по логам
 * звонков — те же правила, что у вопроса в «Новости КЦ» (спека `docs/specs/2026-10-08-call-missed-design.md`).
 * Цифры кликабельные: клик отбирает звонки в детализации ниже (владелец 14.10.26).
 */
function WhoMissed({ r, sel, on }: { r: MissedRating; sel: Sel; on: (p: Sel) => void }) {
  const [week, month] = r.periods;
  const title = (k: Sel["period"]) => (k === "day" ? "За день" : `${k === "week" ? week : month} дней`);
  const before = !r.since || r.day < r.since.slice(0, 10);
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="кто не взял трубку">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Кто не взял трубку</h2>
        <span className="text-[12px] text-fg-muted" data-annot="итоги пропущенных дня">
          {r.calls.total ? (
            <>
              Пропущенных в рабочие часы: {r.calls.total}
              {r.calls.kinds.map((k) => (
                <span key={k.kind} className={k.kind === "ask" ? "text-err" : undefined}>
                  {" · "}
                  {k.label} {k.calls}
                </span>
              ))}
            </>
          ) : before ? (
            r.since ? `Пропущенные разобраны с ${ddmm(r.since)} — за этот день рейтинга нет` : "Пропущенные ещё не разобраны"
          ) : (
            "Пропущенных в рабочие часы за день нет"
          )}
        </span>
      </div>
      {r.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-auto text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr className="text-[12px] text-fg">
                <th />
                {PERIODS.map((k) => (
                  <th
                    key={k}
                    colSpan={4}
                    className="border-l border-border px-3 pt-2 text-center font-semibold"
                    title={k === "day" ? undefined : `За ${title(k)} по выбранный день`}
                  >
                    {title(k)}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="px-4 py-2 font-medium">Сотрудник</th>
                {PERIODS.map((k) => (
                  <Fragment key={k}>
                    <th className="border-l border-border px-3 py-2 text-right font-medium" title="Принятые входящие в рабочие часы">
                      Принял
                    </th>
                    <th className="px-3 py-2 text-right font-medium" title="Был на линии, свободен и не взял трубку">
                      Не взял
                    </th>
                    <th className="px-3 py-2 text-right font-medium" title="Был на линии и свободен, очередь отдавала звонок от 10 с, но телефон не в сети и Bitrix в браузере не отозвался">
                      Не дошло
                    </th>
                    <th className="px-3 py-2 text-right font-medium" title="Не взял ÷ (принял + не взял)">
                      %
                    </th>
                  </Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              {r.rows.map((x) => (
                <tr key={x.user_id} className="border-t border-border" data-annot={`не взял трубку: ${x.name}`}>
                  <td className="whitespace-nowrap px-4 py-2">
                    {x.crm_id ? (
                      <Link to={`/activity/${x.crm_id}?date=${r.day}`} className="hover:underline" title="Смена сотрудника">
                        {x.name}
                      </Link>
                    ) : (
                      x.name
                    )}
                  </td>
                  {PERIODS.map((k) => (
                    <TookMissed key={k} took={x.took[k]} missed={x[k]} unreached={x.unreached[k]} pick={{ user: x.user_id, period: k }} sel={sel} on={on} />
                  ))}
                </tr>
              ))}
              <tr className="border-t-2 border-border font-medium" data-annot="итого не взяли">
                <td className="px-4 py-2 text-fg-muted" title="сумма по сотрудникам: один звонок могли не взять несколько">
                  Все вместе
                </td>
                {PERIODS.map((k) => (
                  <TookMissed
                    key={k}
                    took={r.rows.reduce((n, x) => n + x.took[k], 0)}
                    missed={r.rows.reduce((n, x) => n + x[k], 0)}
                    unreached={r.rows.reduce((n, x) => n + x.unreached[k], 0)}
                    pick={{ user: null, period: k }}
                    sel={sel}
                    on={on}
                  />
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
      <div className="border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
        Не взял — в момент звонка был на линии по учёту времени CRM, не разговаривал, и до него дозванивались от{" "}
        {r.threshold_sec} с, и звонок дошёл: звонил телефон или Bitrix показал вызов. Не дошло — всё то же, но телефон не в сети и
        Bitrix в браузере не отозвался: статус стоял, а звонок до человека не дошёл, в % не входит. Считаем по логам звонков, как в вопросах «Новости КЦ». Принял — входящие, которые он взял в
        рабочие часы 10–21 (своя копия звонков Bitrix). % — доля не взятых среди принятых и не взятых. Клик по цифре — сами
        звонки в детализации ниже.
      </div>
    </section>
  );
}

/** Детализация вкладки: сами непринятые звонки за выбранную цифру, новые сверху. */
function MissedDetail({ r, sel, on, anchor }: { r: MissedRating; sel: Sel; on: (p: Sel) => void; anchor: RefObject<HTMLElement | null> }) {
  const [week, month] = r.periods;
  const days = sel.period === "day" ? 1 : sel.period === "week" ? week : month;
  const from = shiftDay(r.day, days - 1);
  const rows = r.missed.filter((c) => c.kind === sel.kind && (sel.user === null || c.user_id === sel.user) && c.at.slice(0, 10) >= from);
  const имя = (id: number) => r.rows.find((x) => x.user_id === id)?.name ?? `id ${id}`;
  const кто = sel.user === null ? "все сотрудники" : имя(sel.user);
  const когда = days === 1 ? `за ${ddmm(r.day)}` : `за ${days} дней по ${ddmm(r.day)}`;
  // Один звонок могли не взять несколько сотрудников: строк больше, чем самих звонков.
  const звонков = new Set(rows.map((c) => c.id)).size;
  return (
    <section ref={anchor} className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="детализация непринятых">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">Детализация</h2>
        <span className="text-[12px] text-fg-muted">
          {кто} · {когда} · {sel.kind === "unreached" ? "не дошло" : "не взяли"} {rows.length} раз
          {звонков !== rows.length && ` · звонков ${звонков}`}
        </span>
        {(sel.user !== null || sel.period !== "day" || sel.kind !== "ask") && (
          <button
            type="button"
            onClick={() => on({ user: null, period: "day", kind: "ask" })}
            className="ml-auto h-7 rounded-[var(--radius-ui)] border border-border px-2.5 text-[12px] text-fg-muted hover:bg-bg-hover"
          >
            Сбросить отбор
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="px-4 py-3 text-[12.5px] text-fg-muted">
          В этом отборе звонков нет — кликните цифру за {week} или {month} дней.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-auto text-[12.5px]">
            <thead className="text-left text-[11.5px] text-fg-faint">
              <tr>
                <th className="px-4 py-2 font-medium">Когда</th>
                <th className="px-3 py-2 font-medium">{sel.kind === "unreached" ? "Был на линии" : "Не взял"}</th>
                <th className="px-3 py-2 font-medium">Номер клиента</th>
                <th className="px-3 py-2 font-medium">Звонили</th>
                <th className="px-3 py-2 font-medium">Линия</th>
                <th className="px-4 py-2 font-medium">Чем кончилось</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <MissedRow key={`${c.id}-${c.user_id}`} c={c} name={имя(c.user_id)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Вкладка «Кто не взял»: таблица сотрудников и под ней сами звонки. Отдельной вкладкой — решение
 * владельца 14.10.26: в «Неотвеченных» рейтинг стоял между воронкой и таблицей номеров, и было
 * не видно, что детализация есть.
 */
function WhoTab() {
  const [params, setParams] = useSearchParams();
  const day = params.get("date") || today();
  const raw = params.get("period");
  const sel: Sel = {
    user: Number(params.get("user")) || null,
    period: raw === "week" || raw === "month" ? raw : "day",
    kind: params.get("kind") === "unreached" ? "unreached" : "ask",
  };
  const detail = useRef<HTMLElement>(null);
  const rating = useQuery({ queryKey: ["calls-missed", day], queryFn: () => loadMissedRating(day) });
  const set = (next: { day?: string; sel?: Sel }) => {
    const v = { day, sel, ...next };
    const out = new URLSearchParams({ tab: "who", date: v.day });
    if (v.sel.user) out.set("user", String(v.sel.user));
    if (v.sel.period !== "day") out.set("period", v.sel.period);
    if (v.sel.kind !== "ask") out.set("kind", v.sel.kind);
    setParams(out, { replace: true });
  };
  // Прокручиваем к детализации уже после перерисовки: иначе экран едет, пока в ней ещё старые
  // строки, и новый отбор остаётся внизу. Первый показ не трогаем.
  const первый = useRef(true);
  useEffect(() => {
    if (первый.current) {
      первый.current = false;
      return;
    }
    detail.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [sel.user, sel.period, sel.kind]);
  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="день рейтинга">
        <DateRangePicker label="День" single from={day} to={day} onChange={(a) => set({ day: a })} />
      </div>
      {rating.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю, кто не взял трубку…</div>
      ) : rating.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить рейтинг: {String(rating.error.message)}</div>
      ) : (
        <>
          <WhoMissed r={rating.data} sel={sel} on={(p) => set({ sel: p })} />
          <MissedDetail r={rating.data} sel={sel} on={(p) => set({ sel: p })} anchor={detail} />
        </>
      )}
    </>
  );
}

function Report({ r, p, set }: { r: CallsReport; p: ReturnType<typeof useCallsParams>[0]; set: ReturnType<typeof useCallsParams>[1] }) {
  const table = useRef<HTMLElement>(null);
  const pick = (key: string) => {
    set({ filter: key });
    table.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <>
      <NotCalledBack r={r} active={p.filter} onPick={pick} />
      <Funnel r={r} onPick={pick} />
      <Numbers numbers={r.numbers} filter={filterOf(p.filter, r)} sort={p.sort} set={set} anchor={table} />
    </>
  );
}

/** «Неотвеченные»: входящие за день по номерам — прежний экран «Звонки». */
function MissedTab() {
  const [p, set] = useCallsParams();
  const report = useQuery({ queryKey: ["calls-report", p.date], queryFn: () => loadCalls(p.date) });
  return (
    <>
      <div className="flex flex-wrap items-center gap-3" data-annot="день неотвеченных">
        <DateRangePicker label="День" single from={p.date} to={p.date} onChange={(a) => set({ date: a })} />
      </div>
      {report.isSuccess ? (
        <Report r={report.data} p={p} set={set} />
      ) : (
        <>
          {report.isPending ? (
            <div className="text-[12.5px] text-fg-muted">Считаю по CRM…</div>
          ) : (
            <div className="text-[12.5px] text-err">Не удалось получить отчёт: {String(report.error.message)}</div>
          )}
          {/* Кто не взял трубку — своя база и своя вкладка: она живёт, даже когда CRM недоступна. */}
          <p className="text-[12.5px] text-fg-muted">
            Кто не взял трубку — на вкладке «Кто не взял»: она считает по своей базе и не зависит от CRM.
          </p>
        </>
      )}
    </>
  );
}

const WEEKDAY = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const daysAgo = (n: number) => new Date(Date.now() - n * 86_400_000).toLocaleDateString("sv-SE");

const DAYS_GROUP: Record<DaysGroup, { label: string; head: string }> = {
  day: { label: "по дням", head: "День" },
  week: { label: "по неделям", head: "Неделя" },
  month: { label: "по месяцам", head: "Месяц" },
};
const dm = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;

function periodTitle(d: { day: string; to: string }, group: DaysGroup): string {
  if (group !== "month") return `${dm(d.day)} – ${dm(d.to)}`;
  const x = new Date(`${d.day}T12:00:00`);
  const name = `${x.toLocaleDateString("ru-RU", { month: "long" })} ${d.day.slice(0, 4)}`;
  const last = new Date(x.getFullYear(), x.getMonth() + 1, 0).getDate();
  return d.day.slice(8) === "01" && Number(d.to.slice(8)) === last ? name : `${name} · ${dm(d.day)} – ${dm(d.to)}`;
}

/** Исходящие: попытки → номера → соединились → разговоры; % — от чего написано в `of`. */
const OUT_COLS: { key: keyof Outgoing; label: string; hint: string; of?: keyof Outgoing; tone?: string }[] = [
  { key: "calls", label: "Попыток", hint: "исходящих звонков" },
  { key: "phones", label: "Номеров", hint: "уникальных, попыток на номер" },
  { key: "connected", label: "Соединились", hint: "код 200 — ещё не разговор", of: "calls" },
  { key: "talks", label: "Разговоров", hint: "живой разговор по расшифровке", of: "calls", tone: "text-ok" },
  { key: "talk_phones", label: "Дозвонились", hint: "номеров с разговором", of: "phones", tone: "text-ok" },
  { key: "unknown", label: "Не разобрано", hint: "код 200 без статуса разговора" },
];

function OutCells({ o }: { o: Outgoing }) {
  return (
    <>
      {OUT_COLS.map((c, i) => {
        const v = o[c.key];
        return (
          <td key={c.key} className={`whitespace-nowrap px-3 py-1.5 tabular-nums ${i === 0 ? "border-l border-border" : ""}`}>
            {c.key === "unknown" ? (
              <span className={v ? "text-fg-muted" : "text-fg-faint"}>{v || "—"}</span>
            ) : (
              <>
                <span className={`text-[14px] font-semibold ${v ? (c.tone ?? "") : "text-fg-faint"}`}>{v}</span>
                {c.of && v > 0 && <span className={`ml-1.5 text-[12px] ${c.tone ?? "text-fg-muted"}`}>{pct(v, o[c.of])}</span>}
                {c.key === "phones" && v > 0 && (
                  <span className="ml-1.5 text-[12px] text-fg-muted" title="попыток на номер">
                    ×{(o.calls / v).toLocaleString("ru-RU", { maximumFractionDigits: 1 })}
                  </span>
                )}
              </>
            )}
          </td>
        );
      })}
    </>
  );
}

/** Детализация звонков считает CRM одним заходом — не длиннее (`activity.report.MAX_DAYS`). */
const DIAL_MAX_DAYS = 31;
type DialStep = "all" | "talk" | "next" | "zapis" | "deal" | "work" | "refuse";
type DialCol = { step: DialStep; label: string; hint: string; value: (c: DialCount) => number; of?: (c: DialCount) => number; ofHint?: string; tone?: string; sub?: boolean };

const C_CALLS: DialCol = { step: "all", label: "Звонков", hint: "исходящих", value: (c) => c.calls };
const C_TALK: DialCol = { step: "talk", label: "Разговор", hint: "по расшифровке", value: (c) => c.talk, of: (c) => c.calls, ofHint: "от звонков", tone: "text-ok" };
const LEAD_COLS: DialCol[] = [
  C_CALLS,
  C_TALK,
  { step: "next", label: "Дальше", hint: "в сделку", value: (c) => c.zapis + c.deal, of: (c) => c.talk, ofHint: "от разговоров", tone: "text-ok" },
  { step: "zapis", label: "Ждем клиента", hint: "записали", value: (c) => c.zapis, sub: true },
  { step: "deal", label: "Другая стадия", hint: "сделка без записи", value: (c) => c.deal, sub: true },
  { step: "work", label: "В работу", hint: "статус лида", value: (c) => c.work },
  { step: "refuse", label: "Отказ", hint: "закрыли лид", value: (c) => c.refuse, tone: "text-err" },
];
const DIAL_GROUPS: { kind: DialKind; label: string; hint: string; cols: DialCol[] }[] = [
  { kind: "lead_work", label: "Лиды в работе", hint: "уже бывали «В работе» — повторный прозвон", cols: LEAD_COLS },
  { kind: "lead_new", label: "Лиды новые", hint: "ещё не были «В работе» — первая обработка", cols: LEAD_COLS },
  { kind: "deal", label: "Сделки", hint: "сделка была к звонку", cols: [C_CALLS, C_TALK, { step: "zapis", label: "Записали", hint: "→ «Ждем клиента»", value: (c) => c.zapis, of: (c) => c.talk, ofHint: "от разговоров", tone: "text-ok" }] },
  { kind: "rest", label: "Прочие", hint: "без лида и сделки", cols: [C_CALLS, C_TALK] },
];

const spanDays = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00`) - Date.parse(`${a}T12:00:00`)) / 86_400_000) + 1;

function DialCells({ d, from, to }: { d: Dial; from: string; to: string }) {
  const linked = spanDays(from, to) <= DIAL_MAX_DAYS;
  return (
    <>
      {DIAL_GROUPS.map((g) =>
        g.cols.map((c, i) => {
          const n = c.value(d[g.kind]);
          const num = <span className={`text-[14px] font-semibold ${n ? (c.tone ?? "") : "text-fg-faint"}`}>{n}</span>;
          const of = c.of?.(d[g.kind]) ?? 0;
          return (
            <td key={`${g.kind}-${c.step}`} className={`whitespace-nowrap px-2.5 py-1.5 tabular-nums ${i === 0 ? "border-l border-border" : ""} ${c.sub ? "bg-bg/60" : ""}`}>
              {n && linked ? (
                <Link
                  to={`/calls?tab=list&from=${from}&to=${to}&act=${g.kind}${c.step === "all" ? "" : `&astep=${c.step}`}`}
                  className="underline decoration-border underline-offset-2 hover:decoration-fg"
                  title="Открыть эти звонки"
                >
                  {num}
                </Link>
              ) : (
                <span title={n && !linked ? `Звонки открываются за период до ${DIAL_MAX_DAYS} дней` : undefined}>{num}</span>
              )}
              {c.of && n > 0 && <span className={`ml-1.5 text-[12px] ${c.tone ?? "text-fg-muted"}`} title={c.ofHint}>{pct(n, of)}</span>}
            </td>
          );
        }),
      )}
    </>
  );
}

/** Исходящие по лидам (в работе / новые) и сделкам: разговор и чем кончился звонок. Спека `docs/specs/2026-10-19-calls-days-leads-design.md`. */
function DialTable({ rows, total, group, from, to }: { rows: CallsDays["rows"]; total: Dial; group: DaysGroup; from: string; to: string }) {
  return (
    <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="исходящие по лидам и сделкам">
      <div className="overflow-x-auto">
        <table className="w-auto text-[12.5px]">
          <thead className="text-left text-[11.5px] text-fg-faint">
            <tr className="text-[12px] text-fg">
              <th className="px-3 pt-2 pl-4 font-semibold">Исходящие</th>
              {DIAL_GROUPS.map((g) => (
                <th key={g.kind} colSpan={g.cols.length} className="border-l border-border px-2.5 pt-2 font-semibold" data-annot={`исходящие ${g.label}`}>
                  {g.label} <span className="font-normal text-fg-faint">· {g.hint}</span>
                </th>
              ))}
            </tr>
            <tr>
              <th className="px-3 py-2 pl-4 font-medium">{DAYS_GROUP[group].head}</th>
              {DIAL_GROUPS.map((g) =>
                g.cols.map((c, i) => (
                  <th key={`${g.kind}-${c.step}`} className={`whitespace-nowrap px-2.5 py-2 font-medium ${i === 0 ? "border-l border-border" : ""} ${c.sub ? "bg-bg/60" : ""}`}>
                    <div className="text-[12.5px] text-fg-muted">{c.sub ? `↳ ${c.label}` : c.label}</div>
                    <div className="font-normal">{c.hint}</div>
                  </th>
                )),
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => {
              const wd = new Date(`${d.day}T12:00:00`).getDay();
              return (
                <tr key={d.day} className={`border-t border-border hover:bg-bg-hover ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`} data-annot={`исходящие ${d.day}`}>
                  <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">
                    {group === "day" ? (
                      <>
                        {dm(d.day)}
                        <span className="ml-1.5 text-fg-faint">{WEEKDAY[wd]}</span>
                      </>
                    ) : (
                      periodTitle(d, group)
                    )}
                  </td>
                  <DialCells d={d.dial} from={d.day} to={d.to} />
                </tr>
              );
            })}
            <tr className="border-t-2 border-border font-medium" data-annot="исходящие итого">
              <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
              <DialCells d={total} from={from} to={to} />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="space-y-1 border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
        <p>
          Звонок — исходящий из CRM (там его лид и сделка). «Сделки» — сделка уже была к началу звонка; сделка, заведённая в
          разговоре по лиду, — звонок по лиду. Лид «в работе» — уже бывал в статусе «В работе» до звонка (робот возвращает
          такие в «Не обработана» на повторный прозвон); «новый» — ещё ни разу: первая обработка.
        </p>
        <p>
          Итог — что тот же сотрудник сделал в окне звонка (до 15 мин после конца, не дальше его следующего звонка):
          «Дальше» — лид ушёл в сделку: «Ждем клиента» (записали) или другая стадия; иначе последняя правка статуса лида — «В
          работу» или отказ. % у «Дальше» и «Записали» — от разговоров; итог бывает и у звонка без разметки разговора. Число —
          ссылка на эти звонки (за период до {DIAL_MAX_DAYS} дней).
        </p>
      </div>
    </section>
  );
}

/** «Дни»: входящие и исходящие, строка — день, неделя или месяц. Спека `docs/specs/2026-09-26-calls-days-design.md`. */
function DaysTab() {
  const [params, setParams] = useSearchParams();
  const to = params.get("to") || today();
  const from = params.get("from") || daysAgo(13);
  const by: "calls" | "numbers" = params.get("by") === "numbers" ? "numbers" : "calls";
  const rawGroup = params.get("group");
  const group: DaysGroup = rawGroup === "week" || rawGroup === "month" ? rawGroup : "day";
  const set = (next: { from?: string; to?: string; by?: string; group?: DaysGroup }) => {
    const v = { from, to, by, group, ...next };
    const out = new URLSearchParams({ tab: "days", from: v.from, to: v.to });
    if (v.by === "numbers") out.set("by", "numbers");
    if (v.group !== "day") out.set("group", v.group);
    setParams(out, { replace: true });
  };
  const data = useQuery({ queryKey: ["calls-days", from, to, group], queryFn: () => loadCallsDays(from, to, group) });
  const steps = funnelSteps({ long_wait_sec: data.data?.long_wait_sec ?? 20 });
  const rows = data.data?.rows ?? [];
  const peak = Math.max(1, ...rows.map((d) => d.funnel[by].incoming));

  const cells = (f: FunnelData, bar?: boolean) =>
    steps.map((s) => {
      const tone = s.tone?.(f) ?? "";
      return (
        <td key={s.key} className="whitespace-nowrap px-3 py-1.5 tabular-nums">
          <span className={`text-[14px] font-semibold ${tone}`}>{s.value(f)}</span>
          {s.key !== "incoming" && <span className={`ml-1.5 text-[12px] ${tone || "text-fg-muted"}`}>{pct(s.value(f), s.of(f))}</span>}
          {s.key === "incoming" && bar && (
            <span className="ml-2 inline-block h-2 rounded-sm bg-accent/60 align-middle" style={{ width: `${(f.incoming / peak) * 6}rem` }} aria-hidden />
          )}
        </td>
      );
    });

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 text-[12.5px] text-fg-muted" data-annot="период дней">
        <DateRangePicker label="Период" from={from} to={to} onChange={(a, b) => set({ from: a, to: b })} />
        <div className="flex gap-1" role="group" aria-label="Считать входящие">
          {(Object.keys(FUNNEL_ROWS) as ("calls" | "numbers")[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ by: k })}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${by === k ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {FUNNEL_ROWS[k]}
            </button>
          ))}
        </div>
        <div className="flex gap-1" role="group" aria-label="Группировка">
          {(Object.keys(DAYS_GROUP) as DaysGroup[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => set({ group: k })}
              aria-pressed={group === k}
              className={`h-7 rounded-[var(--radius-ui)] border px-2.5 text-[12px] ${group === k ? "border-fg bg-bg text-fg" : "border-border text-fg-muted hover:bg-bg-hover"}`}
            >
              {DAYS_GROUP[k].label}
            </button>
          ))}
        </div>
      </div>
      {data.isPending ? (
        <div className="text-[12.5px] text-fg-muted">Считаю дни…</div>
      ) : data.isError ? (
        <div className="text-[12.5px] text-err">Не удалось получить дни: {String(data.error.message)}</div>
      ) : (
        <section className="rounded-[var(--radius-card)] border border-border bg-bg-subtle" data-annot="воронка по дням">
          <div className="overflow-x-auto">
            <table className="w-auto text-[12.5px]">
              <thead className="text-left text-[11.5px] text-fg-faint">
                <tr className="text-[12px] text-fg">
                  <th />
                  <th colSpan={steps.length} className="px-3 pt-2 font-semibold">
                    Входящие <span className="font-normal text-fg-faint">· {FUNNEL_ROWS[by].toLowerCase()}</span>
                  </th>
                  <th colSpan={OUT_COLS.length} className="border-l border-border px-3 pt-2 font-semibold" data-annot="исходящие">
                    Исходящие
                  </th>
                </tr>
                <tr>
                  <th className="px-3 py-2 pl-4 font-medium">{DAYS_GROUP[group].head}</th>
                  {steps.map((s) => (
                    <th key={s.key} className="whitespace-nowrap px-3 py-2 font-medium">
                      <div className="text-[12.5px] text-fg-muted">{s.label}</div>
                      <div className="font-normal">{s.hint}</div>
                    </th>
                  ))}
                  {OUT_COLS.map((c, i) => (
                    <th key={c.key} className={`whitespace-nowrap px-3 py-2 font-medium ${i === 0 ? "border-l border-border" : ""}`}>
                      <div className="text-[12.5px] text-fg-muted">{c.label}</div>
                      <div className="font-normal">{c.hint}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => {
                  const wd = new Date(`${d.day}T12:00:00`).getDay();
                  const head =
                    group === "day" ? (
                      <>
                        <Link to={`/calls?date=${d.day}`} className="underline decoration-border underline-offset-2 hover:decoration-fg" title="Неотвеченные за этот день">
                          {dm(d.day)}
                        </Link>
                        <span className="ml-1.5 text-fg-faint">{WEEKDAY[wd]}</span>
                      </>
                    ) : (
                      <Link
                        to={`/calls?tab=days&from=${d.day}&to=${d.to}${by === "numbers" ? "&by=numbers" : ""}`}
                        className="underline decoration-border underline-offset-2 hover:decoration-fg"
                        title="Дни этого периода"
                      >
                        {periodTitle(d, group)}
                      </Link>
                    );
                  return (
                    <tr
                      key={d.day}
                      className={`border-t border-border hover:bg-bg-hover ${group === "day" && (wd === 0 || wd === 6) ? "bg-bg" : ""}`}
                      data-annot={`${DAYS_GROUP[group].head.toLowerCase()} ${d.day}`}
                    >
                      <td className="whitespace-nowrap py-1.5 pl-4 pr-3 tabular-nums">{head}</td>
                      {cells(d.funnel[by], true)}
                      <OutCells o={d.outgoing} />
                    </tr>
                  );
                })}
                {data.data && (
                  <tr className="border-t-2 border-border font-medium" data-annot="итого по дням">
                    <td className="py-2 pl-4 pr-3 text-fg-muted">Итого</td>
                    {cells(data.data.total.funnel[by])}
                    <OutCells o={data.data.total.outgoing} />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="space-y-1 border-t border-border px-4 py-2 text-[11.5px] text-fg-faint">
            <p>
              Входящие: числа дня — те же, что на «Неотвеченных» за этот день; клик по дню — туда. Неделя (с понедельника) и
              месяц — сумма их дней, крайние обрезаны периодом; клик — их дни. «Номера» — по первому звонку номера за день, в
              неделе, месяце и «Итого» номер, звонивший в разные дни, считается в каждом. Перезвон по дню ищем 7 дней вперёд —
              свежие дни ещё досчитываются.
            </p>
            <p>
              Исходящие — все исходящие звонки Bitrix (своя копия). Номера в неделе, месяце и «Итого» — уникальные за строку.
              «Соединились» — линия ответила (код 200), это ещё не разговор: там же автоответчики и голосовые ассистенты.
              «Разговоров» — живой разговор с клиентом по расшифровке записи; «не разобрано» — код 200, статуса ещё нет
              (свежие звонки ждут расшифровки) или он неясен. Записей до конца июня 2026 нет — разговоров там не видно.
            </p>
          </div>
        </section>
      )}
      {data.data?.total.dial && <DialTable rows={rows} total={data.data.total.dial} group={group} from={from} to={to} />}
    </>
  );
}

const TABS = [
  { id: "missed", label: "Неотвеченные" },
  { id: "who", label: "Кто не взял" },
  { id: "days", label: "Дни" },
  { id: "list", label: "Звонки" },
] as const;
type Tab = (typeof TABS)[number]["id"];

export function CallsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab");
  const tab: Tab = raw === "list" || raw === "days" || raw === "who" ? raw : "missed";
  // У вкладок свои параметры адреса; день «Неотвеченных», «Кто не взял» и «Звонков» переносим.
  const select = (id: Tab) => {
    if (id === tab) return;
    const day = tab === "days" ? null : params.get(tab === "list" ? "from" : "date");
    const out = new URLSearchParams();
    if (id !== "missed") out.set("tab", id);
    if (id === "list" && day) out.set("from", day);
    if ((id === "missed" || id === "who") && day) out.set("date", day);
    setParams(out, { replace: true });
  };

  return (
    <main className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2 border-b border-border" data-annot="шапка звонков">
        <h1 className="pb-2 text-[16px] font-semibold tracking-[-0.015em]">Звонки</h1>
        <div role="tablist" aria-label="Разделы звонков" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              onClick={() => select(t.id)}
              className={`-mb-px border-b-2 px-3 pb-2 pt-1 text-[13px] ${
                tab === t.id ? "border-accent font-medium text-fg" : "border-transparent text-fg-muted hover:text-fg"
              }`}
              data-annot={`вкладка «${t.label}»`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      {tab === "missed" ? <MissedTab /> : tab === "who" ? <WhoTab /> : tab === "days" ? <DaysTab /> : <CallsListTab />}
    </main>
  );
}
